import {
  forwardRef,
  HTMLAttributes,
  InputHTMLAttributes,
  ChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
} from "react";
import { Search } from "lucide-react";
import { cn } from "~/lib/utils";

// Command Root Component
interface CommandProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const Command = forwardRef<HTMLDivElement, CommandProps>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
      className
    )}
    {...props}
  />
));
Command.displayName = "Command";

// Command Input Component
interface CommandInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onValueChange"> {
  className?: string;
  onValueChange?: (value: string) => void;
}

const CommandInput = forwardRef<HTMLInputElement, CommandInputProps>(
  ({ className, onValueChange, onChange, ...props }, ref) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onValueChange?.(e.target.value);
    };

    return (
      <div className="flex items-center border-b px-3">
        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
        <input
          ref={ref}
          className={cn(
            "flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          onChange={handleChange}
          {...props}
        />
      </div>
    );
  }
);
CommandInput.displayName = "CommandInput";

// Command List Component
interface CommandListProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const CommandList = forwardRef<HTMLDivElement, CommandListProps>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className)}
    {...props}
  />
));
CommandList.displayName = "CommandList";

// Command Empty Component
interface CommandEmptyProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const CommandEmpty = forwardRef<HTMLDivElement, CommandEmptyProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("py-6 text-center text-sm", className)} {...props} />
  )
);
CommandEmpty.displayName = "CommandEmpty";

// Command Group Component
interface CommandGroupProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  heading?: ReactNode;
}

const CommandGroup = forwardRef<HTMLDivElement, CommandGroupProps>(
  ({ className, heading, ...props }, ref) => (
    <div ref={ref} className={cn("overflow-hidden p-1 text-foreground", className)} {...props}>
      {heading && (
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{heading}</div>
      )}
      <div className="space-y-1" {...props} />
    </div>
  )
);
CommandGroup.displayName = "CommandGroup";

// Command Item Component
interface CommandItemProps extends Omit<HTMLAttributes<HTMLDivElement>, "onSelect"> {
  className?: string;
  value?: string;
  onSelect?: (value: string) => void;
  disabled?: boolean;
}

const CommandItem = forwardRef<HTMLDivElement, CommandItemProps>(
  ({ className, value, onSelect, disabled, ...props }, ref) => {
    const handleClick = () => {
      if (!disabled && onSelect && value) {
        onSelect(value);
      }
    };

    const handleKeyDown = (e: ReactKeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-accent hover:text-accent-foreground",
          disabled && "pointer-events-none opacity-50",
          className
        )}
        role="option"
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  }
);
CommandItem.displayName = "CommandItem";

// Command Shortcut Component
interface CommandShortcutProps extends HTMLAttributes<HTMLSpanElement> {
  className?: string;
}

const CommandShortcut = forwardRef<HTMLSpanElement, CommandShortcutProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)}
      {...props}
    />
  )
);
CommandShortcut.displayName = "CommandShortcut";

// Command Separator Component
interface CommandSeparatorProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const CommandSeparator = forwardRef<HTMLDivElement, CommandSeparatorProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("-mx-1 h-px bg-border", className)} {...props} />
  )
);
CommandSeparator.displayName = "CommandSeparator";

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
